import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import consultationService from '../consult/services/consultationService';
import { ConsultationContact, RecentContact } from '../consult/types/consultation';
import Profile from '../../../shared/core/models/profile';
import {
  saveContactsToStorage,
  loadContactsFromStorage,
  saveRecentContacts,
  loadRecentContacts,
  addToRecent as addToRecentStorage,
  shouldRefreshContacts
} from '../consult/utils/storageUtils';

interface ConsultState {
  contacts: ConsultationContact[];
  recentContacts: RecentContact[];
  selectedContact: ConsultationContact | null;
  selectedUserProfile: Profile | null;
  isLoading: boolean;
  isLoadingProfile: boolean;
  error: string | null;
  nextLink: string | null;
  hasMore: boolean;
  totalContacts: number;
}

const initialState: ConsultState = {
  contacts: [],
  recentContacts: [],
  selectedContact: null,
  selectedUserProfile: null,
  isLoading: false,
  isLoadingProfile: false,
  error: null,
  nextLink: null,
  hasMore: true,
  totalContacts: 0,
};

/**
 * Async thunk to fetch contacts
 */
export const fetchContacts = createAsyncThunk(
  'consult/fetchContacts',
  async (args: { nextLink?: string; pageSize?: number; forceRefresh?: boolean } = {}, thunkAPI) => {
    const nextLink = args?.nextLink;
    const pageSize = args?.pageSize || 20;
    const forceRefresh = args?.forceRefresh || false;

    try {
      // Check if we should refresh or use cached data (only for initial load)
      if (!nextLink && !forceRefresh) {
        const cachedContacts = loadContactsFromStorage();
        const needsRefresh = shouldRefreshContacts();

        if (cachedContacts.length > 0 && !needsRefresh) {
          // Return cached data immediately
          thunkAPI.dispatch(setContacts(cachedContacts));
          thunkAPI.dispatch(setRecentContacts(loadRecentContacts()));
          thunkAPI.dispatch(setTotalContacts(cachedContacts.length));
          
          // Fetch in background to update cache
          consultationService.getConsultationContacts(pageSize)
            .then(response => {
              const contacts = response.body.consultationContacts || [];
              const nextState = response.body.nextState || response.body.nextLink || null;
              saveContactsToStorage(contacts);
              thunkAPI.dispatch(setContacts(contacts));
              thunkAPI.dispatch(setTotalContacts(response.body.total || contacts.length));
              thunkAPI.dispatch(setNextLink(nextState));
            })
            .catch(error => {
              console.error('Background contact refresh failed:', error);
            });
          
          return { contacts: cachedContacts, nextLink: null, isLoadMore: false, total: cachedContacts.length };
        }
      }

      // Fetch from API
      const response = await consultationService.getConsultationContacts(pageSize, nextLink);
      const contacts = response.body.consultationContacts || [];
      const newNextLink = response.body.nextState || response.body.nextLink || null;
      const total = response.body.total || contacts.length;
      
      // Save to localStorage (only for initial load or deduplicated)
      if (!nextLink) {
        saveContactsToStorage(contacts);
        const recent = loadRecentContacts();
        thunkAPI.dispatch(setRecentContacts(recent));
      } else {
        saveContactsToStorage(contacts);
      }
      
      return { 
        contacts, 
        nextLink: newNextLink, 
        isLoadMore: !!nextLink,
        total
      };
    } catch (error) {
      if (!nextLink) {
        const cachedContacts = loadContactsFromStorage();
        if (cachedContacts.length > 0) {
          thunkAPI.dispatch(setRecentContacts(loadRecentContacts()));
          return { contacts: cachedContacts, nextLink: null, isLoadMore: false };
        }
      }
      throw error;
    }
  }
);

/**
 * Async thunk to fetch user profile
 */
export const fetchUserProfile = createAsyncThunk(
  'consult/fetchUserProfile',
  async ({ targetId, targetType }: { targetId: string; targetType: string }) => {
    const profile = await consultationService.getUserProfile(targetId, targetType);
    return profile;
  }
);

/**
 * Redux slice
 */
const consultSlice = createSlice({
  name: 'consult',
  initialState,
  reducers: {
    setContacts: (state, action: PayloadAction<ConsultationContact[]>) => {
      state.contacts = action.payload;
    },
    updateContacts: (state, action: PayloadAction<ConsultationContact>) => {
      const contacts = state.contacts.filter((item) => item.targetId !== action.payload.targetId);
      state.contacts = [action.payload, ...contacts];
    },
    setRecentContacts: (state, action: PayloadAction<RecentContact[]>) => {
      state.recentContacts = action.payload;
    },
    setSelectedContact: (state, action: PayloadAction<ConsultationContact | null>) => {
      state.selectedContact = action.payload;
      if (action.payload) {
        // Add to recent when selected
        const updatedRecent = addToRecentStorage(action.payload);
        state.recentContacts = updatedRecent;
      }
    },
    addToRecent: (state, action: PayloadAction<ConsultationContact>) => {
      const updatedRecent = addToRecentStorage(action.payload);
      state.recentContacts = updatedRecent;
    },
    clearSelectedContact: (state) => {
      state.selectedContact = null;
      state.selectedUserProfile = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setNextLink: (state, action: PayloadAction<string | null>) => {
      state.nextLink = action.payload;
      state.hasMore = !!action.payload;
    },
    setTotalContacts: (state, action: PayloadAction<number>) => {
      state.totalContacts = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contacts
      .addCase(fetchContacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        const { contacts, nextLink, isLoadMore, total } = action.payload;
        
        if (isLoadMore) {
          // Append and deduplicate
          const existingIds = new Set(state.contacts.map(c => c.targetId));
          const newContacts = contacts.filter(c => !existingIds.has(c.targetId));
          state.contacts = [...state.contacts, ...newContacts];
        } else {
          state.contacts = contacts;
        }
        
        state.nextLink = nextLink;
        state.hasMore = !!nextLink;
        state.totalContacts = total;
        state.error = null;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch contacts';
      })
      // Fetch user profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoadingProfile = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoadingProfile = false;
        state.selectedUserProfile = action.payload;
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoadingProfile = false;
        state.error = action.error.message || 'Failed to fetch user profile';
      });
  },
});

export const {
  setContacts,
  updateContacts,
  setRecentContacts,
  setSelectedContact,
  addToRecent,
  clearSelectedContact,
  setError,
  setNextLink,
  setTotalContacts,
} = consultSlice.actions;

export default consultSlice;

